import { API_BASE } from '../lib/api-base';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SymphonyPricing } from '../components/SymphonyPricing';
import { ScrollArea } from '../components/symphony/ui';
import { EmbeddedCheckout, getStripe } from '../lib/stripe/embedded';
import { Cadence } from '../lib/plans';
import { useBasePath } from '../lib/use-base-path';
import { useTranslation } from '../lib/i18n';
import { CREDITS_REFRESH_EVENT } from '../lib/use-credits';
import { logPackagePickerView, logPurchasePageView, logPurchaseSuccess } from '../lib/appsflyer';
import { GtmEvent, pushGtmEvent } from '../lib/gtm';
import { useGenericBi } from '../lib/use-generic-bi';
import { usePageLoadBi } from '../lib/use-page-load-bi';
import { useLoadBi } from '../lib/use-load-bi';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { readUpgradeAttribution } from '../lib/upgrade-origin';

// How long a just-created Checkout Session is re-opened instead of minting a new one. Stripe keeps
// a session valid for ~24h; 2 minutes is well inside that and covers the observed burst (8 clicks
// in 3 minutes) while still letting a buyer start a genuinely fresh attempt later.
const CHECKOUT_REUSE_MS = 120_000;
// How long the CTA may stay in its busy state waiting for Stripe's form to mount before it is
// released so the buyer can retry. Generous enough for a slow mobile connection, short enough that
// a failed mount does not look like a hung page.
const CHECKOUT_BUSY_TIMEOUT_MS = 20_000;

// Mount prefix Pepyaka strips in prod; mirrors the other orion-web lib files.
// All three mounts (/orion-web, /symphony-web, /symphony) route their /api/*
// through the same handler, so a single prefix works for either.

// Legacy Wix ("Wixel") pricing page — the fallback package picker shown to a visitor the Symphony
// picker is not (yet) available to (non-US, non-employee; see lib/eligibility.ts). Same destination
// the mobile app opens (features/core/services/upgrade-credits) and the Settings upgrade link uses;
// purchases there reflect in the user's account. Embedded in an iframe rather than redirected to, so
// the visitor stays in-app. Framing is permitted: the page's CSP frame-ancestors allows *.wix.com,
// which covers every mount (symphony.wix.com and manage.wix.com).
const WIXEL_PRICING_URL =
  'https://manage.wix.com/wixel-pricing/select-offer?cycleId=1-YEAR&referralAdditionalInfo=ai_credits_orion';

// Stash of the plan a buyer just sent to Stripe Checkout, so the return page can optimistically show
// the subscribed view for it the instant the payment is confirmed `paid` — before the async webhook
// grants the real subscription. sessionStorage (not React state) because Stripe's return redirect is a
// full navigation that resets in-memory state. Consumed once, on the paid return.
const PENDING_PURCHASE_KEY = 'orion.pendingPurchase';

function readPendingPurchase(): { agentPlanId: string; cadence: Cadence } | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { agentPlanId?: unknown; cadence?: unknown };
    if (typeof parsed.agentPlanId !== 'string') return null;
    return { agentPlanId: parsed.agentPlanId, cadence: parsed.cadence === 'yearly' ? 'yearly' : 'monthly' };
  } catch {
    return null;
  }
}

function clearPendingPurchase() {
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    /* sessionStorage unavailable — nothing to clear */
  }
}

// Drop the Stripe-return query params (?status, ?session_id) from the address bar without a
// navigation, so a manual refresh loads a clean URL and can't replay the checkout-return state.
// history.replaceState (not next/router) is deliberate: it leaves router.query untouched, so the
// in-flight verify/optimistic flow keyed on those values isn't torn down mid-run.
function stripCheckoutParams() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('status') && !url.searchParams.has('session_id') && !url.searchParams.has('billing_lane')) return;
  url.searchParams.delete('status');
  url.searchParams.delete('session_id');
  url.searchParams.delete('billing_lane');
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

// Package picker. Every mount now renders the Symphony pricing design
// (SymphonyPricing, fed by the AgentPlan catalog) — the legacy Orion POC layout
// was retired. Buying opens Stripe **embedded** Checkout in a modal (stays on
// our domain); on completion Stripe returns to ?status=success and the picker
// polls the caller's premium so the new subscription shows up on its own.

export default function PackagesPage() {
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('packages');
  // WHAT SENT THEM HERE. Read once, synchronously, off window.location — `router.query` is empty on the
  // first client render and the view events below fire on mount, so reading it from the router would
  // attribute every view to nothing. `conversationId` is set only by the assistant's links (the halt
  // wall and the model-emitted skill link), and joins this view to the impression that produced it.
  //
  // Without this the picker has one undifferentiated exposure number: when it moves, nothing says which
  // surface moved it, and a prompt change, a new upsell card and a real product change look identical.
  const [attribution] = useState(readUpgradeAttribution);
  // `origin` is a registered src:172 field; `conversation_id` is not on the page-LOAD event (only on the
  // click), so it rides the generic page-view's additionalFields. That is the join key that turns
  // "someone came from the credit wall" into "THIS wall impression produced this view".
  usePageLoadBi(
    { page_name: 'packages', origin: attribution.origin },
    attribution.conversationId ? { additionalFields: { conversation_id: attribution.conversationId } } : true,
  );
  const [pendingId, setPendingId] = useState<string | null>(null);

  // A checkout POST already in flight. `pendingId` cannot guard this on its own: setState is
  // asynchronous and batched, so two clicks landing before the next render both read the old
  // value and both mint a Stripe Checkout Session. A ref is written synchronously, so the second
  // click sees it immediately.
  const checkoutInFlightRef = useRef(false);
  // The last session we successfully created, so repeat clicks on the SAME target re-open that
  // session instead of minting another one.
  //
  // This is not cosmetic tidying. On 2026-08-20 a single buyer produced EIGHT identical
  // `Symphony Basic month $20` sessions in three minutes — some 3 seconds apart — and not one of
  // them reached a payment attempt: the signature of someone clicking again because the modal did
  // not visibly open. Repeat sessions doubled that day (43 vs the usual ~20) and dragged the
  // measured checkout conversion rate from ~34% to ~20% on their own, which is a real number
  // people make decisions from. Every extra click also mints a live Stripe object we never use.
  //
  // Deliberately NOT gated on the modal being open: if the modal failed to render, the buyer must
  // still be able to try again. Re-opening the same session is the safe way to allow that — a
  // Stripe Checkout Session stays valid for ~24h, so reuse costs nothing and the buyer lands
  // exactly where they left off.
  const lastCheckoutRef = useRef<{ key: string; at: number; clientSecret: string; publishableKey: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Whether this visitor may use the picker (US-only for now, Wix employees exempt). null = still
  // resolving (don't flash the plans); false = a real region block (server said allowed:false);
  // 'error' = the eligibility probe itself failed (infra/network) — show a retry state, NOT the
  // misleading "not available in your region" copy, since an eligible US user could land here on a
  // transient blip. The server enforces the same gate on plans/checkout/switch — this only picks UX.
  const [eligible, setEligible] = useState<boolean | 'error' | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [embeddedSecret, setEmbeddedSecret] = useState<string | null>(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [paymentOutcome, setPaymentOutcome] = useState<'verifying' | 'paid' | 'incomplete' | null>(null);
  // The plan a just-confirmed purchase bought (recovered from sessionStorage on a paid return),
  // handed to the picker so it flips to the subscribed view immediately, before the webhook grant.
  const [optimisticPurchase, setOptimisticPurchase] = useState<{ agentPlanId: string; cadence: Cadence } | null>(null);
  const checkoutRef = useRef<EmbeddedCheckout | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);

  const status = typeof router.query.status === 'string' ? router.query.status : undefined;

  // A deeplinked top-up request (?topUpPackageId=…) — the mobile apps open the picker with it to
  // jump straight into the current plan's top-up checkout. Captured once into state and stripped
  // from the address bar immediately (same history.replaceState treatment as the Stripe-return
  // params) so a refresh/back — or the Stripe return redirect — can't replay the auto-open. Read
  // off window.location, not router.query, so it's available on first client render without
  // waiting for router hydration. The login bounce preserves it: redirectToLogin sends the full
  // URL as postLogin, so an unauthenticated open lands back here with the param intact.
  const [autoTopupPackId, setAutoTopupPackId] = useState<string | null>(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    const packId = url.searchParams.get('topUpPackageId');
    if (!packId) return;
    url.searchParams.delete('topUpPackageId');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    setAutoTopupPackId(packId);
  }, []);

  // Drop the attribution params from the address bar once they are captured, the same treatment every
  // other param on this page gets (topUpPackageId above, the Stripe-return trio in stripCheckoutParams).
  // A refresh is not a new hand-off: leaving `origin` in the URL would report a second and third view
  // from one credit wall, and `conversation_id` would keep joining them to the same impression. Safe to
  // do in an effect because `attribution` was read during render, before this runs. history.replaceState,
  // not the router, so nothing keyed on router.query is torn down.
  useEffect(() => {
    if (!attribution.origin && !attribution.conversationId) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('origin');
    url.searchParams.delete('conversation_id');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The eligibility probe gates whether the plans list renders, so track it as the
  // primary plans-list load region (null = in flight, 'error' = probe failed).
  useLoadBi('plans-list', { isLoading: eligible === null, isError: eligible === 'error' });

  // Credits/packages picker viewed (mirrors native's af_content_view). Once per mount. Both
  // destinations carry the origin, so the acquisition funnels split by entry point the same way the
  // src:172 view above does.
  useEffect(() => {
    logPackagePickerView(attribution.origin);
    pushGtmEvent(GtmEvent.packagePickerView, attribution.origin ? { origin: attribution.origin } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On return from Checkout (?status=success&session_id=…), the redirect alone
  // does NOT prove payment — async/incomplete sessions land here too. Retrieve
  // the session and only claim success (SymphonyPricing then polls the premium
  // for the webhook-driven grant) when it actually completed + paid.
  useEffect(() => {
    if (status !== 'success') {
      setPaymentOutcome(null);
      setOptimisticPurchase(null);
      clearPendingPurchase();
      if (status === 'cancelled') stripCheckoutParams();
      return;
    }
    const sessionId = typeof router.query.session_id === 'string' ? router.query.session_id : undefined;
    const billingLane = typeof router.query.billing_lane === 'string' ? router.query.billing_lane : undefined;
    // Strip ?status/?session_id from the address bar now that we've captured what we need. Uses
    // history.replaceState (not router.replace) so Next's router.query is untouched — the outcome
    // still flows through paymentOutcome (React state) this session, but a manual refresh loads a
    // clean URL and can't replay the "payment received" toast.
    stripCheckoutParams();
    if (!sessionId) {
      setPaymentOutcome('incomplete');
      setOptimisticPurchase(null);
      clearPendingPurchase();
      return;
    }
    let cancelled = false;
    setPaymentOutcome('verifying');
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/stripe/session-status?session_id=${encodeURIComponent(sessionId)}${
            billingLane === 'sandbox' ? '&billing_lane=sandbox' : ''
          }`,
          { credentials: 'include' },
        );
        const s = res.ok
          ? ((await res.json()) as {
              status?: string;
              paymentStatus?: string;
              amount?: number | null;
              currency?: string | null;
            })
          : null;
        const paid =
          s?.status === 'complete' && (s?.paymentStatus === 'paid' || s?.paymentStatus === 'no_payment_required');
        if (cancelled) return;
        // Purchase success — BOTH destinations carry the transaction value + currency (web-only:
        // native can't read a Stripe amount). AppsFlyer uses its own `af_revenue`/`af_currency`
        // (inside logPurchaseSuccess); GA4/GTM use the canonical `value`/`currency` params, which
        // its purchase tag reads for revenue reporting and value-based bidding.
        const amount = typeof s?.amount === 'number' ? s.amount : null;
        const currency = s?.currency || null;
        if (paid && amount !== null && currency) {
          logPurchaseSuccess(amount, currency);
        }
        // A 100%-off / no_payment_required purchase still has amount 0 + currency, so it carries
        // value:0. Only a genuinely absent amount falls back to the bare event.
        if (paid) {
          pushGtmEvent(
            GtmEvent.purchaseSuccess,
            amount !== null && currency ? { value: amount, currency } : {},
          );
        }
        setPaymentOutcome(paid ? 'paid' : 'incomplete');
        // Only seed the optimistic subscribed view once Stripe confirms the payment; discard the
        // stash either way so a later manual refresh (no fresh outcome) can't re-apply it.
        setOptimisticPurchase(paid ? readPendingPurchase() : null);
        clearPendingPurchase();
        // Nudge the always-mounted sidebar/settings credit readout to poll for the webhook grant so
        // it updates on its own, rather than showing the pre-purchase balance until a manual refresh.
        if (paid) {
          try {
            window.dispatchEvent(new Event(CREDITS_REFRESH_EVENT));
          } catch {
            /* SSR / no window — the picker poll still reconciles the picker's own view */
          }
        }
      } catch {
        if (!cancelled) {
          setPaymentOutcome('incomplete');
          setOptimisticPurchase(null);
          clearPendingPurchase();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, router.query.session_id, router.query.billing_lane]);

  // Region/employee gate: ask the server whether this visitor may use the picker. A non-OK or
  // non-true answer → blocked (fail-closed), which renders the "not available in your region" state.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/eligibility`, { credentials: 'include' })
      .then(async (r) => {
        if (cancelled) return;
        // Only a 200 is an authoritative allow/deny (the route fails closed to {allowed:false} on a
        // server-side error). A non-200 (e.g. 503 no_bootstrap_context) is an infra hiccup → 'error',
        // not a region block.
        if (!r.ok) {
          setEligible('error');
          return;
        }
        const d = (await r.json()) as { allowed?: boolean };
        setEligible(d?.allowed === true);
      })
      .catch(() => {
        if (!cancelled) setEligible('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Publishable key for embedded Checkout (non-secret; fetched once).
  useEffect(() => {
    fetch(`${API_BASE}/api/stripe/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.publishableKey && setPublishableKey(d.publishableKey))
      .catch(() => {});
  }, []);

  // Mount embedded Checkout once we have both a session client secret and the
  // publishable key. Tears down on close/unmount (Stripe allows one instance).
  useEffect(() => {
    if (!embeddedSecret || !publishableKey) return;
    let destroyed = false;
    let instance: EmbeddedCheckout | null = null;
    (async () => {
      try {
        const stripe = await getStripe(publishableKey);
        const checkout = await stripe.initEmbeddedCheckout({ clientSecret: embeddedSecret });
        if (destroyed) {
          checkout.destroy();
          return;
        }
        instance = checkout;
        checkoutRef.current = checkout;
        if (mountRef.current) checkout.mount(mountRef.current);
        setCheckoutReady(true);
      } catch {
        setError(t('packages.error.loadForm'));
        setEmbeddedSecret(null);
      }
    })();
    return () => {
      destroyed = true;
      try {
        (instance ?? checkoutRef.current)?.destroy();
      } catch {
        /* already torn down */
      }
      checkoutRef.current = null;
      setCheckoutReady(false);
    };
  }, [embeddedSecret, publishableKey]);

  // Release the CTA's busy state the moment the payment form is actually usable — that is the
  // first point the buyer has something to look at, so it is the right moment to stop saying
  // "opening".
  useEffect(() => {
    if (checkoutReady) setPendingId(null);
  }, [checkoutReady]);

  // Safety net: never strand the CTA in its busy state. If the form has not mounted within this
  // window something went wrong (Stripe's script blocked, iframe refused to load), and the buyer
  // must be able to try again rather than stare at a permanently "opening" button. The modal's own
  // loading copy stays on screen, so they can also just close it.
  useEffect(() => {
    if (!pendingId || checkoutReady) return undefined;
    const id = window.setTimeout(() => setPendingId(null), CHECKOUT_BUSY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [pendingId, checkoutReady]);

  // Esc closes the modal (backdrop click already does). Stripe's iframe manages
  // its own internal focus, so we don't add a full focus trap.
  useEffect(() => {
    if (!embeddedSecret) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCheckout();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embeddedSecret]);

  // POST to checkout, open the embedded modal with the returned client secret.
  // The mount prefix rides along so the server returns the user to the picker on
  // the SAME mount they bought from (/orion-web, /symphony-web, or the root). The
  // root mount is the empty string — sent as-is so the server builds a bare-root
  // return URL rather than defaulting to /orion-web.
  const startCheckout = useCallback(
    async (body: Record<string, unknown>, pendingKey: string, reuseKey: string = pendingKey) => {
      // Synchronous — survives React's batching where `pendingId` does not.
      if (checkoutInFlightRef.current) return;
      // Same target, clicked again within the reuse window, and we still hold its client secret:
      // re-open that session rather than creating a second one.
      //
      // `pendingKey` and `reuseKey` are deliberately different. The first identifies the CARD to put
      // in its busy state, so it stays the plain plan id; the second identifies the SESSION, and must
      // carry everything the session was minted with — a session is specific to a cadence and a promo
      // code, and matching on the plan alone re-opened a yearly session for a buyer who had gone back
      // and chosen monthly.
      const cached = lastCheckoutRef.current;
      if (cached && cached.key === reuseKey && Date.now() - cached.at < CHECKOUT_REUSE_MS) {
        setError(null);
        if (cached.publishableKey) setPublishableKey(cached.publishableKey);
        setEmbeddedSecret(cached.clientSecret);
        return;
      }
      checkoutInFlightRef.current = true;
      let opened = false;
      setError(null);
      setPendingId(pendingKey);
      // Purchase page view / checkout opened (mirrors native's af_initiated_checkout).
      logPurchasePageView();
      pushGtmEvent(GtmEvent.purchasePageView);
      try {
        const mount = basePath.replace(/^\//, '');
        const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...body, mount }),
        });
        const json = (await res.json()) as {
          clientSecret?: string;
          publishableKey?: string;
          error?: string;
          message?: string;
        };
        if (!res.ok || !json.clientSecret) {
          // `topup_not_allowed` is the server's plan↔pack enforcement (lib/topup-eligibility) — a
          // bare code, so localize it here rather than showing the raw string. Everything else keeps
          // the service's own message, which usually names the real problem (e.g. a rejected coupon).
          setError(
            json.error === 'topup_not_allowed'
              ? t('packages.error.topupNotAllowed')
              : json.message || json.error || t('packages.error.startCheckout'),
          );
          return;
        }
        // Remember the plan being bought so the return page (a fresh navigation that resets this
        // state) can optimistically flip to the subscribed view once Stripe confirms payment. Only
        // the Symphony (agentPlanId) path — the legacy packageId path has no picker "current plan".
        if (typeof body.agentPlanId === 'string') {
          try {
            sessionStorage.setItem(
              PENDING_PURCHASE_KEY,
              JSON.stringify({ agentPlanId: body.agentPlanId, cadence: (body.cadence as Cadence) ?? 'monthly' }),
            );
          } catch {
            /* sessionStorage unavailable — the optimistic flip just won't apply; poll still catches up */
          }
        }
        // The response key is tied to the same server-resolved metasite mode as the Checkout
        // Session. This replaces a possibly stale page-load key after switching picker sites.
        if (json.publishableKey) setPublishableKey(json.publishableKey);
        lastCheckoutRef.current = {
          key: reuseKey,
          at: Date.now(),
          clientSecret: json.clientSecret,
          publishableKey: json.publishableKey ?? null,
        };
        setEmbeddedSecret(json.clientSecret); // opens the modal; the effect mounts it
        opened = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : t('packages.error.startCheckout'));
      } finally {
        checkoutInFlightRef.current = false;
        // Only drop the button's busy state here when NOTHING opened. On the success path the
        // POST returning is not the moment the buyer sees anything: Stripe's iframe still has to
        // load and mount, and clearing `pendingId` now returns the CTA to its idle label while the
        // screen is still unchanged. That gap is what produced eight sessions from one buyer in
        // three minutes on 2026-08-20. The busy state is released when the form is actually up
        // (or by the safety timeout below), not when the network call finishes.
        if (!opened) setPendingId(null);
      }
    },
    [basePath, t],
  );

  // Check out an AgentPlan option at a cadence (price resolved server-side). `couponCode` is the
  // code the buyer applied on that card — already validated against this exact (plan, cadence)
  // target, but re-validated server-side at CreateCheckoutSession, which is the only place the
  // discount is actually applied. An invalid code there fails the checkout with no Stripe session.
  const buyPlan = useCallback(
    (agentPlanId: string, cadence: Cadence, couponCode?: string) => {
      // The `buy_plan` 198:10 is NOT fired here any more. This callback knows only an AgentPlan id,
      // which is what put a raw GUID in `component_name` and left `component_route` with no cadence
      // in it. Both facts live one level down, in the card and the billing-cycle step, so the event
      // is fired there — see `logBuyPlan` in SymphonyPricing.
      // The reuse key is the whole TARGET, not just the plan.
      //
      // `startCheckout` re-opens a cached Stripe session whenever the key matches inside
      // CHECKOUT_REUSE_MS, and `cadence` and `couponCode` travel in the body where the key could not
      // see them. So "buy, look at the price, go back, pick the other cycle, buy again" re-opened the
      // FIRST session: the buyer chose monthly and Stripe collected a year, silently, for up to 12x
      // the intended amount. The same shape dropped a promo code applied on the second pass.
      //
      // Reachable before the billing-cycle step existed (the cadence toggle could do it), but that
      // step makes going back and changing the cycle a designed part of the flow, with a Back button
      // built for it — so what was a narrow race becomes the ordinary path.
      return startCheckout(
        { agentPlanId, cadence, couponCode },
        agentPlanId,
        `${agentPlanId}:${cadence}:${couponCode ?? ''}`,
      );
    },
    // logPageClickBi is a fresh ref each render; excluded so the callback identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCheckout],
  );

  // Check out a one-time top-up pack (price + credit grant resolved server-side from the
  // AgentPack catalog). No sessionStorage stash: unlike a plan purchase there's no card to flip
  // optimistically — the return page's generic paid handling (success toast + the
  // CREDITS_REFRESH_EVENT nudge to the credit readouts) covers everything a top-up changes.
  const buyTopup = useCallback(
    (packId: string) => {
      logPageClickBi({ page_name: 'packages', button_name: 'buy_topup' }, { componentName: packId });
      return startCheckout({ packId }, packId);
    },
    // logPageClickBi is a fresh ref each render; excluded so the callback identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startCheckout],
  );

  const closeCheckout = () => {
    logPageClickBi({ page_name: 'packages', button_name: 'close_checkout' });
    try {
      checkoutRef.current?.destroy();
    } catch {
      /* already torn down */
    }
    checkoutRef.current = null;
    setEmbeddedSecret(null);
    setCheckoutReady(false);
    setPendingId(null);
  };

  // The embedded-checkout modal.
  const checkoutModal = embeddedSecret ? (
    <ScrollArea
      role="dialog"
      aria-modal="true"
      onClick={closeCheckout}
      className="orion-pane-modal"
      style={{
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 16px',
        // Match the shared Modal's scrim (light dim + blur). `.orion-pane-modal` already scopes this to
        // the content pane, so the sidebar stays crisp; only the plans behind the checkout blur.
        background: 'rgba(var(--ds-black-rgb), 0.28)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 540,
          background: 'var(--ds-surface-sunken)',
          borderRadius: 14,
          padding: 14,
          boxShadow: '0 24px 60px -20px rgba(var(--ds-black-rgb), 0.6)',
        }}
      >
        <button
          type="button"
          onClick={closeCheckout}
          aria-label={t('common.close')}
          style={{
            position: 'absolute',
            top: 6,
            insetInlineEnd: 12,
            zIndex: 1,
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            lineHeight: 1,
            color: 'var(--ds-control-fill-off-hover)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
        {!checkoutReady && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 160,
              color: 'var(--ds-text-placeholder)',
              fontSize: 13,
            }}
          >
            {t('packages.loadingPayment')}
          </div>
        )}
        <div ref={mountRef} />
      </div>
    </ScrollArea>
  ) : null;

  // The MKT pricing design on white — rendered on every mount. 24px bottom padding sets the gap
  // below the bottom-pinned disclaimer footer (SymphonyPricing pins it with margin-top: auto); the
  // full-width footer is centered, so it clears the bottom-right FloatingChat composer bubble.
  return (
    <GenericEventsRouteProvider value="packages">
    <ScrollArea
      component="section"
      className="orion-content-pane"
      style={{ position: 'absolute', top: 0, bottom: 0, zIndex: 4, padding: '72px 40px 24px', background: 'var(--ds-surface-standard-bg)' }}
    >
      <div style={{ maxWidth: 1717, margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        {eligible === false ? (
          // Not available in this region (and not a Wix employee) — the Symphony (Stripe) picker is
          // US-only, so instead of a dead-end message embed the legacy Wix ("Wixel") pricing page so
          // the visitor can still pick a package in place. The server still refuses this account's
          // plans/checkout/switch calls independently; those aren't the Wixel purchase path.
          <iframe
            src={WIXEL_PRICING_URL}
            title={t('packages.wixelFrameTitle')}
            style={{ flexGrow: 1, width: '100%', minHeight: '80vh', border: 0, borderRadius: 14, background: 'var(--ds-surface-sunken)' }}
          />
        ) : eligible === 'error' ? (
          // The eligibility probe failed (infra/network) — NOT a region block. Offer a retry rather
          // than the misleading region copy (an eligible US user could land here on a transient blip).
          <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 440, color: 'var(--ds-text-secondary)' }}>
            <p style={{ fontSize: 14, lineHeight: 1.5 }}>{t('packages.error.availability')}</p>
            <button
              type="button"
              onClick={() => {
                logPageClickBi({ page_name: 'packages', button_name: 'retry' });
                router.reload();
              }}
              style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--ds-separator-default)',
                background: 'var(--ds-surface-sunken)', color: 'var(--ds-surface-sunken-dark)', fontSize: 13, cursor: 'pointer' }}
            >
              {t('common.retry')}
            </button>
          </div>
        ) : eligible === null ? (
          // Still resolving eligibility — don't flash the plans before we know.
          <div style={{ margin: 'auto', color: 'var(--ds-text-placeholder)', fontSize: 13 }}>{t('packages.loadingPayment')}</div>
        ) : (
          <SymphonyPricing
            onCheckout={buyPlan}
            onTopup={buyTopup}
            pendingId={pendingId}
            disabled={pendingId !== null}
            status={paymentOutcome === 'paid' ? 'success' : null}
            optimisticPurchase={paymentOutcome === 'paid' ? optimisticPurchase : null}
            error={error}
            autoTopupPackId={autoTopupPackId}
          />
        )}
      </div>
      {checkoutModal}
    </ScrollArea>
    </GenericEventsRouteProvider>
  );
}
